import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, Phone, User, Clock, Eye, EyeOff, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

interface WixMessage {
  id: string;
  sender_name: string;
  sender_email: string;
  sender_phone: string | null;
  subject: string;
  body: string;
  is_read: boolean;
  received_at: string;
}

export function ReceivedMessages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<WixMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchMessages = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("wix_messages")
      .select("*")
      .eq("user_id", user.id)
      .order("received_at", { ascending: false });

    if (error) {
      toast.error("Erreur lors du chargement des messages");
    } else {
      setMessages(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMessages();
  }, [user]);

  const toggleRead = async (msg: WixMessage) => {
    const { error } = await supabase
      .from("wix_messages")
      .update({ is_read: !msg.is_read })
      .eq("id", msg.id);

    if (!error) {
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, is_read: !m.is_read } : m))
      );
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-foreground">Messages reçus</h2>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {unreadCount} non lu{unreadCount > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={fetchMessages} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Chargement...</div>
      ) : messages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Mail className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>Aucun message reçu pour le moment.</p>
            <p className="text-xs mt-1">Les messages envoyés via votre formulaire de contact apparaîtront ici.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {messages.map((msg) => (
            <Card
              key={msg.id}
              className={`cursor-pointer transition-colors hover:border-primary/30 ${
                !msg.is_read ? "border-primary/40 bg-primary/5" : ""
              }`}
              onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
            >
              <CardHeader className="py-3 px-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!msg.is_read && <div className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                      <CardTitle className="text-sm font-medium truncate">{msg.subject}</CardTitle>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {msg.sender_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(msg.received_at), "dd MMM yyyy HH:mm", { locale: fr })}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRead(msg);
                    }}
                  >
                    {msg.is_read ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </CardHeader>

              {expandedId === msg.id && (
                <CardContent className="pt-0 px-4 pb-4 space-y-3">
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {msg.sender_email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {msg.sender_email}
                      </span>
                    )}
                    {msg.sender_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {msg.sender_phone}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground/90 whitespace-pre-wrap">{msg.body}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
